import { c as _c } from "react/compiler-runtime";
import { Box, Divider, MenuItem, MenuList, Paper, Popper, Select, Stack, type SelectProps } from '@mui/material';
import type { Condition } from '@repo/api-gw-sdk';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/shared/icon';
import { PropertyPolicyRelation, type FilterProperty } from '@/types/advanceFilter';
import { OPERATORS } from './OperatorSelect';
import { inlineDropdownStyle } from './conditionComponents';
interface PropertySelectProps {
  condition?: Condition;
  properties: FilterProperty[][];
  onChange: (condition: Condition) => void;
}
type MenuEntry = {
  kind: 'item';
  property: FilterProperty;
} | {
  kind: 'submenu';
  group: string;
  items: FilterProperty[];
};
const conditionLabel = (property: FilterProperty) => property.condition?.displayName ?? property.displayName;

// Collapse consecutive properties that share the same `condition.subMenu.group`
// into a single fly-out entry, preserving the order of standalone properties.
const buildMenuEntries = (group: FilterProperty[]): MenuEntry[] => {
  const entries: MenuEntry[] = [];
  for (const property of group) {
    const subMenu = property.condition?.subMenu;
    if (!subMenu) {
      entries.push({
        kind: 'item',
        property
      });
      continue;
    }
    const last = entries[entries.length - 1];
    if (last?.kind === 'submenu' && last.group === subMenu.group) {
      last.items.push(property);
    } else {
      entries.push({
        kind: 'submenu',
        group: subMenu.group,
        items: [property]
      });
    }
  }
  return entries;
};

// A fly-out for a sub-menu group, anchored to the right of the parent row
// (`right`). `disablePortal` keeps it a DOM descendant of the row (and of the
// enclosing condition box), so hovering the fly-out keeps both highlighted
// instead of dropping their `:hover`. Closing is debounced so the cursor can
// cross the sibling rows between the row and the fly-out without it closing
// mid-traverse.
const CLOSE_DELAY_MS = 200;
const PropertySubMenu = t0 => {
  const $ = _c(26);
  const {
    group,
    items,
    onSelect
  } = t0;
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
      setOpen(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const openSubMenu = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scheduleClose = t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "var(--mui-palette-primary-dark)"
      }
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
      padding: "6px 16px",
      minHeight: "36px"
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon iconClass="material-symbols-chevron-right-rounded" sx={{
      padding: "0px",
      "& i": {
        margin: "0px",
        color: "currentColor"
      }
    }} />;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== group) {
    t8 = <Stack direction="row" sx={t6}>{group}{t7}</Stack>;
    $[7] = group;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t10;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [{
      name: "flip",
      enabled: false
    }, {
      name: "preventOverflow",
      enabled: false
    }];
    t10 = {
      zIndex: "var(--mui-zIndex-modal)"
    };
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      boxShadow: "var(--mui-customShadows-lg)"
    };
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== items || $[13] !== onSelect) {
    let t13;
    if ($[15] !== onSelect) {
      t13 = property => <MenuItem key={property.name} onClick={event_0 => onSelect(property, event_0)}>{property.condition?.subMenu?.label ?? conditionLabel(property)}</MenuItem>;
      $[15] = onSelect;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    t12 = items.map(t13);
    $[12] = items;
    $[13] = onSelect;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[17] !== t12) {
    t13 = <Paper sx={t11}><MenuList onClick={_temp}>{t12}</MenuList></Paper>;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== anchorEl || $[20] !== open || $[21] !== t13) {
    t14 = <Popper open={open} anchorEl={anchorEl} placement="right" disablePortal={true} modifiers={t9} sx={t10}>{t13}</Popper>;
    $[19] = anchorEl;
    $[20] = open;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14 || $[24] !== t8) {
    t15 = <Box ref={setAnchorEl} onMouseEnter={openSubMenu} onMouseLeave={scheduleClose} sx={t5}>{t8}{t14}</Box>;
    $[23] = t14;
    $[24] = t8;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  return t15;
};
export function PropertySelect(t0) {
  const $ = _c(53);
  let MenuProps;
  let condition;
  let onChange;
  let onClose;
  let onOpen;
  let openProp;
  let properties;
  let props;
  let sx;
  if ($[0] !== t0) {
    ({
      condition,
      properties,
      onChange,
      open: openProp,
      onOpen,
      onClose,
      MenuProps,
      sx,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = MenuProps;
    $[2] = condition;
    $[3] = onChange;
    $[4] = onClose;
    $[5] = onOpen;
    $[6] = openProp;
    $[7] = properties;
    $[8] = props;
    $[9] = sx;
  } else {
    MenuProps = $[1];
    condition = $[2];
    onChange = $[3];
    onClose = $[4];
    onOpen = $[5];
    openProp = $[6];
    properties = $[7];
    props = $[8];
    sx = $[9];
  }
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  condition?.property;
  let t1;
  if ($[10] !== condition?.property || $[11] !== properties) {
    let t2;
    if ($[13] !== condition?.property) {
      t2 = c => c.name === condition?.property;
      $[13] = condition?.property;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    t1 = properties.flat().find(t2);
    $[10] = condition?.property;
    $[11] = properties;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const conditionProperty = t1;
  let t2;
  if ($[15] !== properties) {
    t2 = properties.map(_temp2);
    $[15] = properties;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const entriesByGroup = t2;
  let t3;
  if ($[17] !== condition || $[18] !== onChange) {
    t3 = property => {
      onChange({
        ...condition,
        property: property.name,
        operator: Object.keys(OPERATORS[property.policyRelation ?? PropertyPolicyRelation.MultipleValues])?.[0],
        value: []
      });
    };
    $[17] = condition;
    $[18] = onChange;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const selectProperty = t3;
  const hiddenSelected = conditionProperty?.condition?.subMenu ? conditionProperty : undefined;
  let t4;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      paper: {
        sx: {
          maxHeight: "unset",
          overflow: "visible"
        }
      }
    };
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== MenuProps) {
    t5 = {
      ...MenuProps,
      slotProps: t4
    };
    $[21] = MenuProps;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== sx) {
    t6 = sx ?? {
      ...inlineDropdownStyle,
      height: "100%",
      "& .MuiSelect-select": {
        fontWeight: "medium"
      }
    };
    $[23] = sx;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const t7 = conditionProperty?.name ?? "";
  let t8;
  if ($[25] !== onOpen) {
    t8 = event => {
      setInternalOpen(true);
      onOpen?.(event);
    };
    $[25] = onOpen;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== onClose) {
    t9 = event_0 => {
      setInternalOpen(false);
      onClose?.(event_0);
    };
    $[27] = onClose;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== properties || $[30] !== selectProperty) {
    t10 = event_1 => {
      const property_0 = properties.flat().find(p => p.name === event_1.target.value);
      if (property_0) {
        selectProperty(property_0);
      }
    };
    $[29] = properties;
    $[30] = selectProperty;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== hiddenSelected) {
    t11 = hiddenSelected && <MenuItem key="__selected__" value={hiddenSelected.name} disabled={true} sx={{
      display: "none"
    }}>{conditionLabel(hiddenSelected)}</MenuItem>;
    $[32] = hiddenSelected;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== entriesByGroup || $[35] !== onClose || $[36] !== selectProperty) {
    let t13;
    if ($[38] !== entriesByGroup.length || $[39] !== onClose || $[40] !== selectProperty) {
      t13 = (entries, gi) => {
        const rendered = entries.map(entry => entry.kind === "submenu" ? <PropertySubMenu key={`submenu-${entry.group}`} group={entry.group} items={entry.items} onSelect={(property_1, event_2) => {
          selectProperty(property_1);
          setInternalOpen(false);
          onClose?.(event_2);
        }} /> : <MenuItem key={entry.property.name} value={entry.property.name}>{conditionLabel(entry.property)}</MenuItem>);
        if (gi < entriesByGroup.length - 1) {
          rendered.push(<Divider key={`divider-${gi}`} className="my-[8px]" />);
        }
        return rendered;
      };
      $[38] = entriesByGroup.length;
      $[39] = onClose;
      $[40] = selectProperty;
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    t12 = entriesByGroup.flatMap(t13);
    $[34] = entriesByGroup;
    $[35] = onClose;
    $[36] = selectProperty;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[42] !== open || $[43] !== props || $[44] !== t10 || $[45] !== t11 || $[46] !== t12 || $[47] !== t5 || $[48] !== t6 || $[49] !== t7 || $[50] !== t8 || $[51] !== t9) {
    t13 = <Select MenuProps={t5} sx={t6} value={t7} open={open} onOpen={t8} onClose={t9} onChange={t10} {...props}>{t11}{t12}</Select>;
    $[42] = open;
    $[43] = props;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t5;
    $[48] = t6;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  return t13;
}
function _temp2(group) {
  return buildMenuEntries(group);
}
function _temp(event) {
  return event.stopPropagation();
}